import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { toast } from 'sonner';
import api from '../lib/api';
import AuthFooter from '../components/shared/AuthFooter';

/**
 * ResetPassword — the destination of the reset link inside the email. Reads
 * ?token=… from the URL, validates it against /auth/reset-password/verify on
 * mount, and shows either the new-password form OR a friendly "invalid /
 * expired" state. On success, redirects to /login after a short delay.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';

  const [state, setState] = useState({ phase: 'verifying', email: '', error: '' });
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ phase: 'invalid', email: '', error: 'This reset link is missing its token.' });
      return;
    }
    api.get(`/auth/reset-password/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setState({ phase: 'ready', email: data.email_masked || '', error: '' }))
      .catch((err) => setState({
        phase: 'invalid',
        email: '',
        error: err.response?.data?.detail || 'Invalid or expired reset link.',
      }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw1 !== pw2) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw1 });
      setState((s) => ({ ...s, phase: 'done' }));
      toast.success('Password updated. Redirecting to sign in…');
      setTimeout(() => nav('/login'), 1600);
    } catch (err) {
      // Token may have been consumed / expired between verify and submit.
      const msg = err.response?.data?.detail || 'Reset failed. Try requesting a fresh link.';
      toast.error(msg);
      if (/expired|used|invalid/i.test(msg)) {
        setState({ phase: 'invalid', email: '', error: msg });
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center mb-8">
          <BrandLogo height={38} />
        </Link>

        <div className="fab-card p-8" data-testid="reset-password-card">
          {state.phase === 'verifying' && (
            <div className="text-center py-6" data-testid="reset-verifying">
              <Loader2 className="w-8 h-8 animate-spin text-fab-primary mx-auto" />
              <p className="text-[13px] text-fab-text-secondary mt-3">Verifying your reset link…</p>
            </div>
          )}

          {state.phase === 'invalid' && (
            <div className="text-center" data-testid="reset-invalid">
              <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-bold text-fab-text mt-4">Link no longer valid</h1>
              <p className="text-[13px] text-fab-text-secondary mt-2 leading-relaxed">{state.error}</p>
              <Link
                to="/forgot-password"
                className="btn-primary inline-block mt-5 px-5 py-2.5 text-[13px]"
                data-testid="reset-request-new"
              >
                Request a new link
              </Link>
              <div className="mt-4">
                <Link to="/login" className="text-[13px] text-fab-text-secondary font-semibold hover:text-fab-primary">
                  Back to Sign in
                </Link>
              </div>
            </div>
          )}

          {state.phase === 'ready' && (
            <>
              <h1 className="font-display text-2xl font-bold text-fab-text">Choose a new password</h1>
              <p className="text-[13px] text-fab-text-secondary mt-1">
                Resetting the password for <span className="font-mono text-fab-text">{state.email}</span>.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-[12px] font-semibold text-fab-text-secondary">New Password</label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fab-text-muted" />
                    <input
                      type={show ? 'text' : 'password'}
                      value={pw1}
                      onChange={(e) => setPw1(e.target.value)}
                      placeholder="At least 8 characters"
                      className="fab-input w-full pl-9 pr-10 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]"
                      data-testid="reset-new-password"
                      required
                      autoFocus
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-fab-text-muted">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-fab-text-secondary">Confirm New Password</label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fab-text-muted" />
                    <input
                      type={show ? 'text' : 'password'}
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      placeholder="Re-enter password"
                      className="fab-input w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]"
                      data-testid="reset-confirm-password"
                      required
                    />
                  </div>
                  {pw2 && pw1 !== pw2 && (
                    <div className="text-[11px] text-red-500 mt-1 font-semibold" data-testid="reset-mismatch">Passwords do not match.</div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting || !pw1 || !pw2}
                  className="btn-primary w-full py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reset-submit"
                >
                  {submitting ? 'Updating…' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {state.phase === 'done' && (
            <div className="text-center" data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-bold text-fab-text mt-4">Password updated</h1>
              <p className="text-[13px] text-fab-text-secondary mt-2">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
        <AuthFooter />
      </div>
    </div>
  );
}
